/**
 * Module dependencies.
 */

import { useMutation } from '@tanstack/react-query';
import { media } from '@untile/react-core/styles/media';
import { AxiosError } from 'axios';
import { GetStaticProps, NextPage } from 'next';
import { serverSideTranslations } from 'next-i18next/pages/serverSideTranslations';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { RecoverPasswordData, recoverPassword } from 'src/api/authentication/recover-password';
import { Button } from 'src/components/core/button';
import { InputField } from 'src/components/core/forms/input/field';
import { SEO } from 'src/components/core/seo';
import { Description, Form, Title } from 'src/components/layout/authentication/styles';
import { inputMasks } from 'src/core/constants/forms';
import { nifRules } from 'src/core/utils/fields-rules';
import { getNetworkErrorTranslatedKey } from 'src/core/utils/requests';
import { toast } from 'src/providers/toast';
import { SSGProps } from 'src/types/app';
import styled from 'styled-components';

/**
 * `StyledInputField` styled component.
 */

const StyledInputField = styled(InputField)`
  margin-bottom: 8px;

  ${media.min.ms`
    margin-bottom: 24px;
  `}
` as typeof InputField;

/**
 * `RecoverPassword` page.
 */

const RecoverPassword: NextPage = () => {
  const { t } = useTranslation();
  const {
    control,
    formState: { isSubmitting },
    handleSubmit,
    reset
  } = useForm<RecoverPasswordData>({ mode: 'onSubmit' });

  const { isPending, mutate } = useMutation({ mutationFn: recoverPassword });
  const onSubmit = useCallback(
    (data: RecoverPasswordData) => {
      mutate(data, {
        onError: networkError => {
          const error = networkError as AxiosError;
          const errorMessage = getNetworkErrorTranslatedKey({ error });

          toast.error(t(errorMessage, { returnObjects: true }));
        },
        onSuccess: () => {
          toast.success(t('recover-password:form.successMessage', { returnObjects: true }));

          reset();
        }
      });
    },
    [mutate, reset, t]
  );

  return (
    <>
      <SEO title={t('recover-password:metatags.title')} />

      <Title>{t('recover-password:title')}</Title>

      <Description>{t('recover-password:description')}</Description>

      <Form autoComplete={'off'} onSubmit={handleSubmit(onSubmit)}>
        <StyledInputField
          control={control}
          disabled={isSubmitting}
          label={t('auth:form.labels.nif')}
          mask={inputMasks.nif}
          name={'nif'}
          rules={nifRules(t)}
        />

        <Button isLoading={isSubmitting || isPending} stretch type={'submit'}>
          {t('recover-password:actions.submit')}
        </Button>
      </Form>
    </>
  );
};

/**
 * Export `getStaticProps`.
 */

export const getStaticProps: GetStaticProps<SSGProps> = async ({ locale }) => {
  return {
    props: {
      ...(await serverSideTranslations(locale as string, ['common', 'auth', 'recover-password'])),
      layout: 'auth'
    },
    revalidate: 60
  };
};

/**
 * Export `RecoverPassword` page.
 */

export default RecoverPassword;
